"use client";

import { usePathname } from "next/navigation";

import { ProfileAvatar } from "@/app/(discover)/components/sidebar/profile-avatar";
import { getNavigationItems, NavigationItem } from "@/lib/navigation-config";
import { User } from "@/lib/types/auth";
import { cn } from "@/lib/utils";
import { useAuth } from "@/stores/use-auth";

function isRouteActive(pathname: string, url: string): boolean {
  return pathname === url || pathname.startsWith(`${url}/`);
}

function getActiveClassName(isActive: boolean): string {
  return cn(
    isActive
      ? "bg-surface-strong text-foreground"
      : "text-muted-foreground hover:bg-surface-subtle hover:text-foreground",
  );
}

interface NavigationListProps {
  items: NavigationItem[];
  pathname: string;
}

function NavigationList({ items, pathname }: NavigationListProps) {
  return (
    <div className="space-y-2">
      {items.map((item) => {
        const isActive = isRouteActive(pathname, item.url);

        return (
          <item.Component
            key={item.testId}
            {...item}
            className={cn(
              "flex flex-col items-center justify-center p-3 rounded-lg transition-colors relative",
              getActiveClassName(isActive),
            )}
          />
        );
      })}
    </div>
  );
}

interface NavigationContainerProps {
  items: NavigationItem[];
  pathname: string;
  user: User | null;
}

function MobileNavBar({ items, pathname, user }: NavigationContainerProps) {
  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-40 pb-safe">
      <div className="flex items-center justify-around h-16 px-2">
        {items.map((item) => {
          const isActive = isRouteActive(pathname, item.url);

          return (
            <item.Component
              key={item.testId}
              {...item}
              className={cn(
                "relative flex flex-col items-center justify-center flex-1 h-full transition-colors min-w-[60px]",
                isActive
                  ? "text-foreground"
                  : "text-muted-foreground active:text-foreground",
              )}
            />
          );
        })}
        {user && (
          <ProfileAvatar
            user={user}
            size="xs"
            className="relative flex flex-col items-center justify-center flex-1 h-full transition-colors min-w-[60px] text-muted-foreground active:text-foreground"
          />
        )}
      </div>
    </nav>
  );
}

function DesktopSidebar({ items, pathname, user }: NavigationContainerProps) {
  return (
    <div className="hidden lg:flex w-24 bg-card border-r border-border flex-col fixed top-14 sm:top-16 left-0 bottom-0 z-40">
      <div className="p-2 pt-4">
        <NavigationList items={items} pathname={pathname} />
      </div>
      {user && (
        <div className="p-2">
          <ProfileAvatar
            user={user}
            size="lg"
            className="flex flex-col items-center justify-center p-3 rounded-lg transition-colors hover:bg-surface-subtle group"
          />
        </div>
      )}
      <div className="flex-1" />
    </div>
  );
}

export function AppSidebar() {
  const items = getNavigationItems();
  const { user } = useAuth();
  const pathname = usePathname();

  return (
    <>
      <MobileNavBar items={items} pathname={pathname} user={user} />
      <DesktopSidebar items={items} pathname={pathname} user={user} />
    </>
  );
}
